import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, viewChild } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { NgOptimizedImage } from '@angular/common';
import { PopoverModule, Popover } from 'primeng/popover';
import { TableModule, TablePageEvent } from 'primeng/table';
import {
  SystemNotification,
  SystemNotificationGroup,
  SystemNotificationsService,
} from '@/layouts/system-notifications/system-notifications.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';
import { BadgeModule } from 'primeng/badge';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, merge, of } from 'rxjs';
import { NotificationQuery } from '@shared/models/notification-query';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { PersonalizationService } from '@/shared/services/personalization.service';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TimeAgoPipe } from "../../shared/pipes/time-ago.pipe";

@Component({
  selector: 'cis-system-notifications',
  imports: [
    DialogModule,
    PopoverModule,
    TableModule,
    NgOptimizedImage,
    BadgeModule,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    TimeAgoPipe
],
  template: `
    @if (profile()) {
      @let count = notificationsCount();
      <div class="flex justify-content-center align-items-center">
        <span class="p-overlay-badge" (click)="op.toggle($event)">
          <i class="pi pi-bell text-3xl cursor-pointer"></i>
          @if (count > 0) {
            <p-badge severity="info" [value]="count" />
          }
        </span>
      </div>
      <p-popover
        #op
        [style]="{ width: '620px', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }"
        (onShow)="onShow()">
        <div class="notification-header">
          <h5 class="m-0">{{ 'common.notifications' | translate }}</h5>
          <a (click)="$event.stopPropagation(); markAllAsRead()">{{ 'common.mark_all_as_read' | translate }}</a>
        </div>

        <p-table
          #dt
          lazy
          class="p-datatable"
          [value]="notifications()"
          [rowHover]="true"
          [paginator]="true"
          [rows]="formGroup.controls.pageSize.getRawValue()"
          [rowsPerPageOptions]="[5, 10, 25]"
          [totalRecords]="totalRecords()"
          paginatorStyleClass="justify-content-end"
          (onPage)="onPage($event)">
          <ng-template #body let-notification>
            <tr class="cursor-pointer" (click)="$event.stopPropagation(); navigateToEntity(notification)">
              <td class="p-0">
                <img ngSrc="/branding/logos/regular/SVG/Mark.svg" height="70" width="70" alt="fim" />
              </td>
              <td>
                <div class="notification-content">
                  <h5>
                    {{ notification.subject }}
                    @if (!notification.isRead) {
                      <p-button
                        size="small"
                        severity="success"
                        text
                        icon="pi pi-check"
                        [pTooltip]="'common.mark_as_read' | translate"
                        tooltipPosition="top"
                        (onClick)="$event.stopPropagation(); markAsRead(notification.id)" />
                    }
                  </h5>
                  <p [innerHTML]="notification.description"></p>
                  <span class="notification-time">{{ notification.createdDate | timeAgo }}</span>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="2">{{ 'notifications.no_notifications' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-popover>
    }
  `,
  styleUrls: ['system-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemNotificationsComponent {
  private readonly systemNotificationsService = inject(SystemNotificationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly personalizationService = inject(PersonalizationService);

  profile = toSignal(this.personalizationService.profiles$.pipe(map((t) => t?.id ?? null)), {
    initialValue: null,
  });

  overlayPanel = viewChild<Popover>('op');

  formGroup = new FormGroup({
    pageNumber: new FormControl(1, { nonNullable: true }),
    pageSize: new FormControl(5, { nonNullable: true }),
    searchTerm: new FormControl<string | null>(null),
    orderByField: new FormControl<string | undefined>('createdDate', {
      nonNullable: true,
    }),
    isAsc: new FormControl<boolean>(false),
    showRead: new FormControl<boolean>(false, { nonNullable: true }),
  });

  notificationsResponse = toSignal(
    merge(
      this.formGroup.valueChanges.pipe(debounceTime(300)),
      this.systemNotificationsService.refresher$.pipe(map(() => this.formGroup.getRawValue())),
    ).pipe(
      map((value) => {
        const copyValue = { ...value };
        for (const k in copyValue) {
          const key = k as keyof typeof copyValue;
          if (copyValue[key] === undefined || copyValue[key] === null) {
            delete copyValue[key];
          }
        }
        return copyValue as NotificationQuery;
      }),
      switchMap((value) => {
        const profiile = this.profile();
        if (!profiile) {
          return of({
            data: {
              items: [],
              totalRecords: 0,
            },
          });
        }
        return this.systemNotificationsService.getByQuery(value);
      }),
    ),
  );

  totalRecords = computed(() => {
    const response = this.notificationsResponse();
    if (!response) {
      return 0;
    }
    return response.data.totalRecords;
  });

  notifications = computed(() => {
    const response = this.notificationsResponse();
    if (!response) {
      return [];
    }
    return response.data.items;
  });

  onPage(event: TablePageEvent) {
    this.formGroup.controls.pageSize.patchValue(event.rows, {
      emitEvent: false,
    });
    this.formGroup.controls.pageNumber.patchValue(event.first / event.rows + 1);
  }

  markAllAsRead() {
    this.systemNotificationsService.markAllAsRead().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  markAsRead(id: number) {
    this.systemNotificationsService.markAsRead(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  notificationsCount = computed(() => this.notificationsResponse()?.data?.totalRecords ?? 0);

  navigateToEntity({ id, groupId, entityId }: SystemNotification) {
    this.markAsRead(id);
    let entityType: string;

    switch (groupId) {
      case SystemNotificationGroup.DataRequest:
        entityType = 'datarequests';
        break;
      case SystemNotificationGroup.QuestionnaireRequest:
        entityType = 'questionnaireRequests';
        break;
      default:
        entityType = 'datatemplates';
        break;
    }
    this.router.navigate([`/${entityType}/${entityId}`]).then(() => {
      this.overlayPanel()?.hide();
    });
  }

  onShow() {
    const content = document.getElementsByClassName('p-popover');
    setTimeout(() => {
      if (content) {
        content[0].scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 20);
  }
}
